import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import type { ColorName } from '@hivebrite/design-system/Theme';

import type { StyledImageProps } from 'hb-builder/components/BuilderComponents/page/block/Gallery/types';
import LoadingBox from 'hb-builder/components/common/LoadingComponents/LoadingBox';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { BACKGROUND_POSITION } from 'hb-builder/constants/imageArrangement';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type {
  GridItemAttributes,
  Image,
} from 'hb-react/backoffice/types/builder/itemAttributes/galleryItemAttributes';

const GridItem: BuilderComponent<GridItemAttributes> = () => <div />; // never used

interface GridItemUIProps extends Omit<Image, 'url'> {
  backgroundPosition?: string;
  fontFamily?: string;
  isLoadingSrc?: boolean;
  item: DocumentItem<GridItemAttributes>;
  textColor?: ColorName;
  toggleDrawer?: (arg: DocumentItem<GridItemAttributes>) => void;
  url?: string;
}

const GridItemUI = ({
  backgroundPosition,
  caption,
  fontFamily,
  imageAlt,
  item,
  isLoadingSrc,
  mr,
  mt,
  size,
  subdomainNetworkUrl,
  textColor,
  toggleDrawer,
  url,
}: GridItemUIProps) => {
  if (!url) {
    return null;
  }

  const { _imageData } = item.attributes || {};

  const fullImageUrl =
    url.startsWith('/images/missing') && subdomainNetworkUrl
      ? `${subdomainNetworkUrl}${url}`
      : url;

  return (
    <StyledImageWrapper
      $size={size}
      mr={mr}
      mt={mt}
      onClick={() => {
        toggleDrawer?.(item);
      }}
    >
      <StyledImage
        $bgPos={backgroundPosition || BACKGROUND_POSITION.CENTER}
        $hasCaption={!!caption}
        $image={_imageData || fullImageUrl}
        $size={size}
      >
        {isLoadingSrc && (
          <LoadingBox>
            <Loader size={40} />
          </LoadingBox>
        )}
        {imageAlt && <span role="img" aria-label={imageAlt} />}
      </StyledImage>
      {caption && (
        <StyledCaption color={textColor || 'white'} $fontFamily={fontFamily}>
          {caption}
        </StyledCaption>
      )}
    </StyledImageWrapper>
  );
};

const RenderBuilder = () => {
  const { getParentItem, updateAttribute } = useBuilderContext();
  const { item } = useComponentContext();
  const {
    hivebrite: { networkFont, subdomainNetworkUrl },
  } = useBuilderConfigContext();

  const parentItem = getParentItem(item.id);
  const { index, ...image } = item.attributes || {};
  const { fontFamily: parentFont, textColor } = parentItem?.attributes || {};

  const handleToggleDrawer = (image) => {
    const isOpen = parentItem.attributes.__isSelected === image.id;

    updateAttribute({
      id: parentItem.id,
      attribute: '__isSelected',
      value: isOpen ? null : image.id,
    });
  };

  const commonProps: GridItemUIProps = {
    toggleDrawer: handleToggleDrawer,
    item,
    ...image,
    fontFamily: parentFont || networkFont,
    textColor,
    subdomainNetworkUrl,
  };

  return <GridItemUI {...commonProps} />;
};

const Render = ({ item }: RenderProps<DocumentItem<GridItemAttributes>>) => {
  const { fontFamily, subdomainNetworkUrl, textColor } = useRendererContext();
  const newAttributes = {
    ...item?.attributes,
    fontFamily,
    textColor,
    subdomainNetworkUrl,
  };

  if (!item) {
    return null;
  }

  return <GridItemUI item={item} {...newAttributes} />;
};

GridItem.componentName = 'GridItem';
GridItem.componentType = 'single';

GridItem.canBeDoubleClicked = true;
GridItem.canBeDragged = true;
GridItem.canBeListed = true;
GridItem.canBeSelected = true;

GridItem.RenderBuilder = RenderBuilder;
GridItem.Render = Render;

GridItem.item = ({ fontFamily, image, index, textColor }) => ({
  attributes: {
    fontFamily,
    index,
    textColor,
    ...image,
  },
  type: GridItem.componentName,
});

export default GridItem;

interface StyledImageWrapperProps {
  $size: GridItemUIProps['size'];
}

const StyledImageWrapper = styled(Box).attrs<StyledImageWrapperProps>(
  ({ $size }) => ({
    height: {
      s: $size === 'big' ? '210px' : '122px',
      m: $size === 'big' ? '344px' : '160px',
    },
    position: 'relative',
    width: {
      s: '100%',
      m: $size === 'big' ? 'calc(40%)' : '100%',
      l: $size === 'big' ? 'calc(40% + 2px)' : '100%',
      xl: $size === 'big' ? 'calc(40% + 9px)' : '100%',
    },
  }),
)<StyledImageWrapperProps>``;

const StyledImage = styled(Box).attrs(() => ({
  height: '100%',
  width: '100%',
}))<StyledImageProps>`
  background-image: ${({ $image }) => `url(${$image})`};
  background-position: ${({ $bgPos }) => $bgPos};
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 10px;
  position: relative;

  &:after {
    border-radius: 10px;
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.3);
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.6) 0%,
      rgba(0, 0, 0, 0) ${({ $size }) => ($size === 'big' ? '15%' : '30%')}
    );
    opacity: ${({ $hasCaption }) => ($hasCaption ? 1 : 0)};
  }
`;

interface StyledCaptionProps {
  $fontFamily: GridItemAttributes['fontFamily'];
}

const StyledCaption = styled(Box).attrs(() => ({
  bottom: '7px',
  left: '8px',
  position: 'absolute',
}))<StyledCaptionProps>`
  font-family: ${({ $fontFamily }) => $fontFamily};
  word-break: break-word;
`;
